import React, { useState } from "react";
import { Modal, FormActions } from "../../shared";
import { appointmentsApi } from "../../../services";
import { Appointment, PaymentMethod } from "../../../types";
import { getPaymentMethodColor } from "../../../utils/appointmentUtils";
import { PaymentMethodBalanceIcon } from "../../../utils/paymentMethodIcons";

interface UpdatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedAppointment?: Appointment) => void;
  appointment: Appointment | null;
}

export const UpdatePaymentModal: React.FC<UpdatePaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  appointment,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">(appointment?.payment || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!appointment) {
    return null;
  }

  const paymentMethods: PaymentMethod[] = ["PIX", "Débito", "Crédito", "Dinheiro"];

  const handleSubmit = async () => {
    if (!paymentMethod) {
      setError("Por favor, selecione um método de pagamento");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await appointmentsApi.updatePayment(
        appointment.id,
        paymentMethod as PaymentMethod
      );

      if (response.success && response.data) {
        onSuccess(response.data);
        onClose();
        setPaymentMethod("");
      } else {
        setError(response.error || "Erro ao atualizar pagamento");
      }
    } catch (err) {
      console.error("Error updating payment:", err);
      setError("Erro ao atualizar pagamento");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentMethod("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Atualizar Pagamento"
      size="md"
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-2">
            Cliente: <span className="font-semibold">{appointment.client?.name || "Cliente"}</span>
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Valor: <span className="font-semibold">R$ {appointment.price ? parseFloat(appointment.price).toFixed(2) : "0.00"}</span>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Método de Pagamento
          </label>
          <div className="grid grid-cols-2 gap-3">
            {paymentMethods.map((method) => {
              const isSelected = paymentMethod === method;
              const baseColors = getPaymentMethodColor(method);
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setPaymentMethod(method)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium border-2 transition-all ${
                    isSelected
                      ? `${baseColors} border-opacity-100 shadow-md scale-105`
                      : `${baseColors} border-opacity-30 hover:border-opacity-50 hover:shadow-sm opacity-70 hover:opacity-100`
                  }`}
                >
                  <span className="flex shrink-0 items-center justify-center [&>.MuiSvgIcon-root]:block">
                    <PaymentMethodBalanceIcon method={method} size={22} />
                  </span>
                  {method}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitText="Atualizar Pagamento"
          cancelText="Cancelar"
          loading={loading}
        />
      </div>
    </Modal>
  );
};
